import React, { useEffect, useRef } from 'react';
import Separator from './Separator';
import Button from './Button';
import Panel from './Panel';
import Spacer from './Spacer';
import { CustomEventTypes, LayoutOrientations, NativeElementTags } from '../../globals';
import { ClearIcon, Chevron } from '../Iconset';
import filterPropsEvents from '../reactUtils/filterPropsEvents';
import { getUuidv4 } from '../../classes/utilities/Misc';
import { KeyCodes } from '../../interfaces/Input';

interface IDialogAction
{
	/** the text that will be displayed on the button */
	label: string;
	/** an optional icon to display alongside the label */
	icon?: JSX.Element;
	/** if true, the button will be disabled */
	disabled?: boolean;
	/** a callback function that will be executed when the button is clicked */
	onClick?: (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	/** if true, the button will be styled as the primary action */
	primary?: boolean;
}

interface IDialogProps
{
	/** a visual display for use as the title of the dialog */
	title: React.ReactNode;
	/** a callback function that will be executed when the dialog - and sequently the underlying modal - needs to be closed */
	onShutdown?: () => void;
	/** these will be displayed within the dialog's content container */
	children?: React.ReactNode;
	/** an optional array of possible actions the dialog presents to the user, e.g. confirm, cancel, etc. */
	actions?: Array<IDialogAction>;
	/** an additional CSS class name to apply to the dialog root div */
	className?: string;
	/** optional CSS properties to add or override the default styling */
	style?: React.CSSProperties;
	/** if true, an 'X' button will be displayed along with the title to allow the user to close the dialog without taking any of the actions */
	showCloseButton?: boolean;
	/** used for automation purposes to specifically identify this property. *defaults to "dialog-root". optional* */
	"data-name"?: string;
	/** optionsl CSS properties to add or override the default styling of the dialog's content container */
	contentStyle?: React.CSSProperties;
	/** if true, a back button will be displayed along with the title. For use with multi-page dialogs */
	showBackButton?: boolean;
	/** a callback function that will be executed when the back button is clicked */
	onBackButtonClick?: () => void;
	/** provided by the ModalManager, and called whenever the modal is removed. honestly not sure why we have both this one and onShutdown */
	onModalCloseCallback?: () => void;
	/** generic input handling */
	onClick?: () => void;
	/** if true, the dialog will not automatically focus on the first input */
	stopAutoFocus?: boolean;
}

/**
 * basically a visual wrapper for Modals that have more complex interaction or require the user's full attention
 *
 * @export
 * @param {IDialogProps} props
 * @return {*} a stylized modal dialog displaying a title, content, and possible actions to take
 */
export default function Dialog(props: IDialogProps)
{
	const selfRef = useRef<HTMLDivElement>(null);
	const guid = useRef<string>(getUuidv4());
	const previouslyFocused = useRef<Element | null>(document.activeElement);

	useEffect(() =>
	{
		if (!props.stopAutoFocus && selfRef.current)
		{
			document.getElementById(guid.current)?.focus();
		}
	}, [selfRef.current]);

	function shutDown()
	{
		if (previouslyFocused.current)
			(previouslyFocused.current as HTMLElement)?.focus();

		if (props.onShutdown)
		{
			props.onShutdown();
		}
	}

	function generateActions(): Array<JSX.Element>
	{
		if(!props.actions) return [];

		const output = new Array<JSX.Element>;

		let i = 0;
		props.actions.forEach(action =>
		{
			output.push(
				<Button
					silent={true}
					key={i}
					iconRenderer={action.icon}
					label={action.label}
					data-name={"dialog-action-" + action.label}
					disabled={action.disabled}
					onClick={(e) =>
					{
						if (action.onClick)
						{
							action.onClick(e);
						}

						shutDown();
					}}
				/>
			);

			i++;
		});

		return output;
	}

	return (
		<div
			ref={selfRef}
			id={guid.current}
			tabIndex={0}
			className={[
				"dialogRoot",
				(props.className ? props.className : ""),
			].join(' ')}
			data-name={props["data-name"] ?? "dialog-root"}
			style={props.style}
			{...filterPropsEvents(props)}
			onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => 
			{
				if (document.activeElement?.tagName === "TEXTAREA")
				{
					return;
				}
				if (e.key === "Enter")
				{
					const primaryAction = props.actions?.find(action => action.primary === true) ?? props.actions?.at(0);
					if(primaryAction?.onClick && primaryAction?.disabled !== true)
					{
						primaryAction.onClick();
						shutDown();
					}
				}
				if (e.key === "Escape")
				{
					shutDown();
				}
			}}
		>
			<Panel orientation={LayoutOrientations.Horizontal}
				className={"dialogTitleBar"}
			>
				{
					!props.showBackButton ? null :
						<>
							<Chevron data-name={"dialog-back-button"} onClick={props.onBackButtonClick} rotate={90} />
							<Spacer size={{ x: "6px" }} />
						</>
				}
				<div className={"dialogTitle hugeText"}>
					{props.title}
				</div>
				<Spacer stretchTo={{ x: "100%" }} />
				{
					!props.showCloseButton ? null :
						<ClearIcon data-name={"dialog-close-button"}
							onClick={() =>
							{
								shutDown();
							}}
						/>
				}
			</Panel>
			<Separator className={"solid"}
				orientation={LayoutOrientations.Horizontal}
			/>
			<Panel className={"dialogContent" + (!props.actions ? " noActions" : "")} style={props.contentStyle}>
				{props.children}
			</Panel>
			{
				!props.actions ? null :
					<div className={"dialogActionsBar"}>
						{
							generateActions()
						}
					</div>
			}
		</div>
	);
}